'use client'

import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {cn} from '../../../../lib/utils'

export const SIDEBAR_WIDTH_EXPANDED = 224
export const SIDEBAR_WIDTH_COLLAPSED = 72
export interface SidebarItem {
    id: string
    label: React.ReactNode
    icon?: React.ReactNode
    href?: string
    active?: boolean
    onClick?: (id: string) => void
    submenu?: SidebarItem[]
    defaultOpen?: boolean
}

interface SidebarProps {
    items: SidebarItem[]
    expandNavigationLabel: string
    collapseNavigationLabel: string
    headerSlot?: React.ReactNode
    footerSlot?: React.ReactNode
    collapsed?: boolean
    onCollapsedChange?: (collapsed: boolean) => void
    className?: string
    contentClassName?: string
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                   items,
                                                   expandNavigationLabel,
                                                   collapseNavigationLabel,
                                                   headerSlot,
                                                   footerSlot,
                                                   collapsed = false,
                                                   onCollapsedChange,
                                                   className,
                                                   contentClassName
                                               }) => {
    const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(() => {
        const initial: Record<string, boolean> = {}
        items.forEach(item => {
            if (item.submenu?.length) {
                initial[item.id] = Boolean(item.defaultOpen)
            }
        })
        return initial
    })

    const toggleSection = (id: string, open?: boolean) => {
        setOpenSections(prev => ({...prev, [id]: open ?? !prev[id]}))
    }

    React.useEffect(() => {
        setOpenSections(prev => {
            const next: Record<string, boolean> = {}
            items.forEach(item => {
                if (!item.submenu?.length) {
                    return
                }
                if (item.defaultOpen) {
                    next[item.id] = true
                    return
                }
                if (prev[item.id] !== undefined) {
                    next[item.id] = prev[item.id]
                    return
                }
                next[item.id] = false
            })
            return next
        })
    }, [items])

    const renderItem = (item: SidebarItem, depth = 0) => {
        const isTopLevel = depth === 0
        const hasSubmenuPanel = Boolean(item.submenu?.length)
        const hasSubmenu = !collapsed && hasSubmenuPanel
        const isOpen = hasSubmenu && (openSections[item.id] ?? item.defaultOpen ?? false)
        const isActive = item.active || item.submenu?.some(sub => sub.active)
        const labelText = typeof item.label === 'string' ? item.label : undefined
        const isSubActive = depth > 0 && isActive

        const itemClasses = cn(
            'relative w-full flex items-center rounded-lg',
            // The button this link replaced had the global 44px touch target.
            hasSubmenu && 'min-h-[44px]',
            isTopLevel ? 'gap-3' : 'gap-2',
            isTopLevel ? 'px-3 py-2.5 text-[15px] font-semibold' : 'px-3 py-2 text-sm font-medium',
            'transition-[background-color,color,box-shadow,filter] duration-200',
            isTopLevel && isActive && 'bg-[linear-gradient(16deg,var(--fitro-brand-gradient-primary-600)_0%,var(--fitro-brand-gradient-secondary-500)_100%)] text-white shadow-[0_14px_28px_rgba(57,98,198,0.24)] dark:bg-none dark:bg-gray-800 dark:font-bold dark:shadow-none hover:brightness-[1.04]',
            isSubActive && 'bg-white/75 text-[var(--fitro-brand-primary-600)] shadow-sm ring-1 ring-[rgba(203,221,255,0.7)] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-full before:bg-[linear-gradient(180deg,var(--fitro-brand-gradient-primary-500)_0%,var(--fitro-brand-gradient-secondary-500)_100%)] dark:bg-gray-800/80 dark:text-primary-100 dark:ring-gray-700',
            !isActive && 'text-[var(--fitro-brand-neutral-700)] hover:bg-white/70 hover:text-[var(--fitro-brand-primary-600)] hover:shadow-sm dark:text-gray-200 dark:hover:bg-gray-800',
            depth > 0 && 'pl-3',
            collapsed && isTopLevel && 'pl-4'
        )

        const itemContent = (
            <>
                {item.icon && <span className={cn('flex shrink-0 items-center justify-center', isTopLevel ? 'h-5 w-5' : 'h-4 w-4')}>{item.icon}</span>}
                <span className={cn(
                    'min-w-0 truncate text-left transition-opacity duration-(--duration-fast) ease-out motion-reduce:transition-none',
                    collapsed && isTopLevel && 'opacity-0',
                    isTopLevel && isActive && 'text-white'
                )}>
                    {item.label}
                </span>
                {hasSubmenu && (
                    <svg
                        className={cn(
                            'w-4 h-4 transition-transform duration-normal ease-out motion-reduce:transition-none ml-auto',
                            isActive ? 'text-white' : 'text-[var(--fitro-brand-neutral-500)]',
                            isOpen && 'rotate-180'
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                )}
            </>
        )

        return (
            <div key={item.id}>
                {item.href ? (
                    <Link
                        href={item.href}
                        prefetch={item.href === '/' ? false : undefined}
                        // Closed: open and navigate. Open: collapse without navigating, so the route cannot re-open it.
                        onClick={hasSubmenu ? (event) => {
                            if (isOpen) event.preventDefault()
                            toggleSection(item.id, !isOpen)
                        } : undefined}
                        className={itemClasses}
                        title={labelText}
                        aria-label={labelText}
                        aria-expanded={hasSubmenu ? isOpen : undefined}
                        // A section is only an ancestor of the current page; its active sub-item carries aria-current.
                        aria-current={isActive && !hasSubmenu ? 'page' : undefined}
                    >
                        {itemContent}
                    </Link>
                ) : (
                    <button
                        onClick={() => {
                            if (hasSubmenu) toggleSection(item.id)
                            item.onClick?.(item.id)
                        }}
                        className={itemClasses}
                        title={labelText}
                        aria-label={labelText}
                    >
                        {itemContent}
                    </button>
                )}

                {hasSubmenuPanel && (
                    <div
                        className={cn(
                            'grid transition-[grid-template-rows,opacity] duration-(--duration-normal) ease-out motion-reduce:transition-opacity',
                            isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                        )}
                        inert={!isOpen}
                        aria-hidden={!isOpen}
                    >
                        <div className="min-h-0 overflow-hidden -mx-1 px-1">
                            <div className="mt-1.5 ml-2 pl-2 pb-0.5 space-y-1.5 border-l border-[rgba(203,221,255,0.72)] dark:border-gray-700">
                                {item.submenu!.map(sub => renderItem(sub, depth + 1))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const renderCollapseIcon = () => (
        <svg
            className={cn(
                'w-5 h-5 transition-transform duration-normal ease-out motion-reduce:transition-none',
                collapsed && 'rotate-180'
            )}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
            />
        </svg>
    )

    return (
        <nav
            className={cn(
                'fixed left-0 top-0 h-full z-40',
                'bg-[linear-gradient(180deg,var(--fitro-brand-surface-lavender)_0%,var(--fitro-brand-surface-blue)_46%,#FFFFFF_100%)] dark:bg-gray-950 dark:bg-none',
                'border-r border-[rgba(203,221,255,0.72)] dark:border-gray-700 shadow-[var(--fitro-brand-shadow)] dark:shadow-lg',
                'hidden desktop:flex flex-col overflow-hidden',
                className
            )}
            style={{width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED}}
        >
            <div className="flex flex-col h-full">
                <div className="relative flex h-20 shrink-0 items-center border-b border-[rgba(203,221,255,0.72)] px-5 dark:border-gray-700">
                    <Link
                        href="/home"
                        className={cn(
                            'mx-auto flex shrink-0 items-center rounded-md transition-opacity duration-(--duration-fast) ease-out motion-reduce:transition-none focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--fitro-brand-primary-500)]',
                            collapsed && 'pointer-events-none opacity-0'
                        )}
                        aria-label="Fitro"
                        inert={collapsed}
                        aria-hidden={collapsed}
                    >
                        <Image
                            src="/logo.svg"
                            alt="Fitro"
                            width={176}
                            height={54}
                            className="h-12 w-auto dark:hidden"
                            loading="eager"
                        />
                        <Image
                            src="/logo-dark.svg"
                            alt="Fitro"
                            width={176}
                            height={54}
                            className="hidden h-12 w-auto dark:block"
                            loading="eager"
                        />
                    </Link>

                    <button
                        type="button"
                        onClick={() => onCollapsedChange?.(!collapsed)}
                        className={cn(
                            'flex h-10 w-10 items-center justify-center rounded-full',
                            'absolute top-1/2 -translate-y-1/2',
                            collapsed ? 'right-3.5' : 'right-3',
                            'bg-white/65 text-[var(--fitro-brand-neutral-500)] shadow-sm ring-1 ring-[rgba(203,221,255,0.72)]',
                            'transition-[background-color,color] duration-200 hover:bg-white hover:text-[var(--fitro-brand-primary-600)]',
                            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--fitro-brand-primary-500)]',
                            'dark:bg-gray-800/80 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                        )}
                        aria-label={collapsed ? expandNavigationLabel : collapseNavigationLabel}
                        title={collapsed ? expandNavigationLabel : collapseNavigationLabel}
                    >
                        {renderCollapseIcon()}
                    </button>
                </div>

                {headerSlot && (
                    <div className="shrink-0 px-4 pb-4">
                        {headerSlot}
                    </div>
                )}

                <div className={cn('flex-1 overflow-y-auto px-2.5 py-5 space-y-2.5', contentClassName)}>
                    {items.map(item => renderItem(item))}
                </div>

                {footerSlot && (
                    <div className="shrink-0 px-4 pb-3 pt-2 border-t border-[rgba(203,221,255,0.72)] dark:border-gray-700">
                        {footerSlot}
                    </div>
                )}
            </div>
        </nav>
    )
}
